import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { assumeEmployeeRole } from '../../../stores/slices/employees/employeesActions';
import {
  deleteCoachUser,
  getCoachUsers,
} from '../../../stores/slices/userAccess/userAccessActions';
import { filterByValue } from '../../../utils/filterByValue';
import DeleteAlert from '../../Alerts/DeleteAlert';
import UserAccessModal from './UserAccessModal';

const Access = () => {
  const { employees, isLoading } = useSelector(state => state.access);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isAssumming, setIsAssumming] = useState(false);
  const [selectedEmployeeToAssume, setSelectedEmployeeToAssume] =
    useState(null);
  const [selectedEmployeeToRemove, setSelectedEmployeeToRemove] =
    useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelRef = useRef();
  const dispatch = useDispatch();
  const searchRef = useRef();
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlertAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  useEffect(() => {
    dispatch(getCoachUsers());
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setFilteredEmployees(employees);
  }, [employees]);

  const filter = data => {
    const text = data.target.value;
    setFilteredEmployees(filterByValue(employees, text));
  };

  const clearInput = () => {
    searchRef.current.value = '';
    setFilteredEmployees(employees);
  };

  const assumeRole = async id => {
    setIsAssumming(true);
    const res = await dispatch(assumeEmployeeRole({ id }));
    if (res.type === 'employees/assumeEmployeeRole/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      navigate('/dashboard');
      toast({
        description: 'User assumed',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
    setIsAssumming(false);
    setSelectedEmployeeToAssume(null);
  };

  const onDeleteItem = async () => {
    setIsDeleting(true);
    const res = await dispatch(
      deleteCoachUser({ id: selectedEmployeeToRemove })
    );
    setSelectedEmployeeToRemove(null);
    onCloseAlert();
    setIsDeleting(false);
    if (res.type === 'access/deleteCoachUser/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      toast({
        description: 'Access removed',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  const onCloseModal = () => {
    onClose();
    dispatch(getCoachUsers());
  };
  return (
    <React.Fragment>
      <Box w={'full'} overflowX={'hidden'} overflowY={'auto'}>
        <VStack spacing={'4'}>
          <Stack
            justifyContent={'space-between'}
            alignContent={'center'}
            direction={{ base: 'column', sm: 'row' }}
            w={'full'}
            spacing={4}
            pr={'2'}
            pb={'2'}
            zIndex={'10'}
            sx={{
              position: '-webkit-sticky',
              /* Safari */
              // eslint-disable-next-line
              position: 'sticky',
              top: '0',
            }}
            bg={'white'}
            borderBottom={'1px'}
            borderColor={'gray.500'}
          >
            <InputGroup size="sm" maxW={{ base: '100%', md: '40%' }}>
              <Input
                pr="4.5rem"
                placeholder="Search"
                borderRadius={'4'}
                onChange={filter}
                ref={searchRef}
              />
              <InputRightElement width="4.5rem" justifyContent={'end'}>
                <Button
                  size={'sm'}
                  borderLeftRadius={'none'}
                  onClick={clearInput}
                >
                  Clear
                </Button>
              </InputRightElement>
            </InputGroup>
            <Button size={'sm'} onClick={onOpen}>
              Access Request
            </Button>
          </Stack>
          <Box overflowX={'auto'} w={'full'}>
            <TableContainer>
              <Table
                variant="striped"
                colorScheme="gray"
                display={{
                  base: 'block',
                  md: 'table',
                }}
              >
                <Thead>
                  <Tr>
                    <Th>Organisation</Th>
                    <Th>Employee</Th>
                    <Th>Email</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {employees &&
                    (isLoading ? (
                      <Tr>
                        <Td colSpan={'5'}>
                          <Spinner color="primary" />
                        </Td>
                      </Tr>
                    ) : employees.length > 0 ? (
                      filteredEmployees.map(employee => (
                        <Tr key={employee.id}>
                          <Td>{employee.organisation.name}</Td>
                          <Td>{employee.full_name}</Td>
                          <Td>{employee.email}</Td>
                          <Td align={'right'} textAlign={'right'}>
                            <HStack justify={'end'}>
                              <Button
                                isLoading={
                                  isAssumming &&
                                  selectedEmployeeToAssume === employee.id
                                }
                                onClick={() => {
                                  setSelectedEmployeeToAssume(employee.id);
                                  assumeRole(employee.id);
                                }}
                              >
                                Assume Role
                              </Button>
                              <Button
                                isLoading={
                                  isDeleting &&
                                  selectedEmployeeToRemove === employee.id
                                }
                                bg={'red.400'}
                                _hover={{
                                  color: 'red.400',
                                  bg: 'transparent',
                                }}
                                onClick={() => {
                                  setSelectedEmployeeToRemove(employee.id);
                                  onOpenAlertAlert();
                                }}
                              >
                                Remove Access
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={'5'}>No employees found</Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </VStack>
      </Box>
      <DeleteAlert
        isOpen={isOpenAlert}
        cancelRef={cancelRef}
        onClose={onCloseAlert}
        isDeleting={isDeleting}
        header={`Remove user access?`}
        body={''}
        deleteText={'Remove'}
        onDeleteItem={onDeleteItem}
      />
      {isOpen && (
        <UserAccessModal
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onCloseModal}
        />
      )}
    </React.Fragment>
  );
};

export default Access;
